import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, AlertTriangle, Info, MessageSquare, Users, Lock } from 'lucide-react';

export default function Disclaimer() {
  const lastUpdated = "April 4, 2026";

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0A0A1B 0%, #0D0D1F 100%)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ background: 'rgba(10, 10, 27, 0.8)', borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">Back to Privluxe</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" style={{ color: '#7C3AED' }} />
            <span className="text-sm" style={{ color: 'rgba(229,231,235,0.6)' }}>Legal</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.2) 0%, rgba(239,68,68,0.2) 100%)', border: '1px solid rgba(245,158,11,0.3)' }}>
            <AlertTriangle className="h-8 w-8" style={{ color: '#F59E0B' }} />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Disclaimer</h1>
          <p style={{ color: 'rgba(229,231,235,0.6)' }}>Last updated: {lastUpdated}</p>
        </div>

        {/* Main Disclaimer */}
        <div className="p-8 rounded-2xl mb-12" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4" style={{ color: '#F59E0B' }} />
            <p className="text-xl font-semibold text-white mb-4">Important Notice</p>
            <p className="text-lg" style={{ color: '#E5E7EB' }}>
              Privluxe is a communication tool only.<br />
              <span style={{ color: 'rgba(229,231,235,0.6)' }}>We are not responsible for user interactions, relationships, or outcomes arising from the use of our platform.</span>
            </p>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-10">
          {/* Service Disclaimer */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.2)' }}>
                <Info className="h-5 w-5" style={{ color: '#A78BFA' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Service Disclaimer</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>Privluxe provides a platform for private communications. By using our service, you acknowledge and agree that:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The service is provided "as is" without warranties of any kind</li>
                <li>We do not guarantee uninterrupted or error-free service</li>
                <li>We are not responsible for the content of user communications</li>
                <li>Technical limitations may affect service availability or functionality</li>
                <li>Features may change or be discontinued without prior notice</li>
              </ul>
            </div>
          </section>

          {/* User Interactions */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)' }}>
                <Users className="h-5 w-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">User Interactions</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>Privluxe is not responsible for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The accuracy, reliability, or legality of information shared by users</li>
                <li>The conduct, behavior, or actions of any user</li>
                <li>Any relationships, agreements, or transactions between users</li>
                <li>Disputes or conflicts arising between users</li>
                <li>Any harm resulting from user interactions, whether online or offline</li>
                <li>Content shared by users, including messages, media, or links</li>
              </ul>
              <p className="mt-4">Users interact with each other at their own risk. Exercise caution when communicating with people you don't know personally.</p>
            </div>
          </section>

          {/* Privacy Limitations */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.2)' }}>
                <Lock className="h-5 w-5" style={{ color: '#22C55E' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Privacy Limitations</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>While we design our platform with privacy in mind:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>No digital communication system is 100% secure</li>
                <li>Recipients may screenshot, record, or share your messages</li>
                <li>Third-party devices or networks may have vulnerabilities</li>
                <li>We may be required to disclose information to comply with legal obligations</li>
                <li>Metadata and technical data may be processed for service operation</li>
              </ul>
            </div>
          </section>

          {/* Content Disclaimer */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.2)' }}>
                <MessageSquare className="h-5 w-5" style={{ color: '#F59E0B' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Content Disclaimer</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>Regarding content on or through our platform:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Users are solely responsible for content they create, upload, or share</li>
                <li>We do not endorse, verify, or guarantee user-generated content</li>
                <li>Content may be inaccurate, misleading, offensive, or harmful</li>
                <li>We are not liable for any decisions made based on platform content</li>
                <li>External links are provided for convenience only; we don't control external sites</li>
              </ul>
            </div>
          </section>

          {/* No Professional Advice */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.2)' }}>
                <AlertTriangle className="h-5 w-5" style={{ color: '#EF4444' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">No Professional Advice</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>Nothing on Privluxe constitutes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Legal, financial, medical, or professional advice</li>
                <li>A substitute for professional consultation</li>
                <li>An endorsement of any product, service, or course of action</li>
              </ul>
              <p className="mt-4">Always seek appropriate professional advice for important decisions.</p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section>
            <div className="p-6 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <h3 className="text-lg font-semibold text-white mb-3">Limitation of Liability</h3>
              <p style={{ color: 'rgba(229,231,235,0.7)' }}>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, PRIVLUXE AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF DATA, PROFITS, OR GOODWILL, ARISING FROM YOUR USE OF OR INABILITY TO USE THE SERVICE.
              </p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section>
            <div className="p-6 rounded-xl" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
              <h3 className="text-lg font-semibold text-white mb-3">Your Acknowledgment</h3>
              <p style={{ color: 'rgba(229,231,235,0.7)' }}>
                By using Privluxe, you acknowledge that you have read, understood, and agree to this Disclaimer. You accept full responsibility for your use of the platform and any consequences arising therefrom.
              </p>
            </div>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-16 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <h3 className="text-lg font-semibold text-white mb-4">Related Documents</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/terms" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
              <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#A78BFA' }} />
              <span className="text-sm text-white">Terms & Conditions</span>
            </Link>
            <Link to="/privacy" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)' }}>
              <Lock className="h-6 w-6 mx-auto mb-2" style={{ color: '#22D3EE' }} />
              <span className="text-sm text-white">Privacy Policy</span>
            </Link>
            <Link to="/acceptable-use" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <Users className="h-6 w-6 mx-auto mb-2" style={{ color: '#22C55E' }} />
              <span className="text-sm text-white">Acceptable Use</span>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p style={{ color: 'rgba(229,231,235,0.4)' }}>© 2026 Privluxe. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
